import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { Link } from "@remix-run/react";
import { Clock } from "lucide-react";

import PublicLeagueCard from "~/components/cards/PublicLeagueCard";
import { getAuthenticatedUser } from "~/services/auth.server";
import { defaultMetaTagsBuilder } from "~/utils/meta";
import { getAllPublicLeagues } from "~/services/league/league.server";

export const links: LinksFunction = () => [
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&family=Barlow:wght@400;500;600&display=swap",
  },
];

export const meta: MetaFunction = ({ location }) => {
  const url = new URL(location.pathname, "https://app.7qz.io");
  return defaultMetaTagsBuilder({
    title: "Home",
    description: "View all the exciting leagues on 7Qz",
    url: url,
  });
};

export async function loader({ request }: LoaderFunctionArgs) {
  const allPublicLeagues = await getAllPublicLeagues();
  const user = await getAuthenticatedUser(request);

  if (!allPublicLeagues.success || !allPublicLeagues.payload) {
    throw new Error("Could not get public leagues");
  }

  const activeLeagues = allPublicLeagues.payload.filter((league) => {
    return league.closing_date_time
      ? league.closing_date_time.getTime() >= new Date().getTime()
      : false;
  });

  const inactiveLeagues = allPublicLeagues.payload.filter((league) => {
    return league.closing_date_time
      ? league.closing_date_time.getTime() < new Date().getTime()
      : false;
  });

  return {
    payload: {
      activeLeagues,
      inactiveLeagues,
    },
    user,
  };
}

const css = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-7px); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: .35; transform: scale(.65); }
  }
  .hero-content-anim { animation: fadeUp .6s ease both; }
  .empty-icon-anim   { animation: float 3s ease-in-out infinite; }
  @media (max-width: 700px) {
    .hero-section  { padding: 60px 20px 60px !important; }
    .main-content  { padding: 0 16px 60px !important; }
    .how-strip     { grid-template-columns: 1fr !important; }
    .empty-stats   { gap: 16px !important; }
  }
`;

const T = {
  bg: "#0a0a0b",
  surface: "#141416",
  border: "rgba(255,255,255,0.07)",
  teal: "#00c5a0",
  tealDim: "rgba(0,197,160,0.12)",
  tealBorder: "rgba(0,197,160,0.28)",
  text: "#f0f0f2",
  muted: "#8a8a95",
  display: "'Barlow Condensed', sans-serif",
  body: "'Barlow', sans-serif",
  radius: 12,
} as const;

export default function Index() {
  const { payload, user } = useLoaderData<typeof loader>();

  const totalPlayers = payload.activeLeagues.reduce(
    (sum, league) => sum + league._count.bets,
    0
  );

  return (
    <>
      <style>{css}</style>

      {/* ── HERO ─────────────────────────────────────────── */}
      <section
        className="hero-section"
        style={{
          position: "relative",
          overflow: "hidden",
          padding: "96px 32px 80px",
          minHeight: 460,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* decorative background */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <div
            style={{
              position: "absolute",
              width: 700,
              height: 700,
              borderRadius: "50%",
              background: T.teal,
              filter: "blur(90px)",
              opacity: 0.16,
              top: -260,
              left: -140,
            }}
          />
          <div
            style={{
              position: "absolute",
              width: 500,
              height: 500,
              borderRadius: "50%",
              background: "#2563eb",
              filter: "blur(90px)",
              opacity: 0.16,
              bottom: -200,
              right: -80,
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
              maskImage:
                "radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)",
              WebkitMaskImage:
                "radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)",
            }}
          />
        </div>

        <div
          className="hero-content-anim"
          style={{
            position: "relative",
            textAlign: "center",
            maxWidth: 640,
          }}
        >
          <span
            style={{
              display: "inline-block",
              marginBottom: 20,
              padding: "5px 16px",
              borderRadius: 100,
              background: T.tealDim,
              border: `1px solid ${T.tealBorder}`,
              color: T.teal,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: ".1em",
              textTransform: "uppercase",
              fontFamily: T.body,
            }}
          >
            Free to play · Any sport
          </span>

          <h1
            style={{
              fontFamily: T.display,
              fontSize: "clamp(56px, 9vw, 96px)",
              fontWeight: 800,
              lineHeight: 0.92,
              letterSpacing: "-.01em",
              textTransform: "uppercase",
              color: "#fff",
              marginBottom: 22,
            }}
          >
            Pick 3.{" "}
            <span style={{ color: T.teal, display: "block" }}>
              Beat the crowd.
            </span>
          </h1>

          <p
            style={{
              fontFamily: T.body,
              fontSize: 17,
              lineHeight: 1.65,
              color: T.muted,
              maxWidth: 440,
              margin: "0 auto 32px",
            }}
          >
            One set of rules. Every sport. No odds, no bookmaker — just pick any
            3 outcomes, rack up points, win prizes.
          </p>

          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/signup"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "14px 30px",
                borderRadius: 10,
                background: T.teal,
                color: T.bg,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: "none",
                fontFamily: T.body,
                whiteSpace: "nowrap",
                transition: "opacity .15s, transform .15s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.opacity = "0.85";
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.opacity = "1";
                (e.currentTarget as HTMLElement).style.transform = "none";
              }}
            >
              Play for Free
            </Link>
            <a
              href="#how-it-works"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "14px 30px",
                borderRadius: 10,
                background: "rgba(255,255,255,0.05)",
                border: `1px solid ${T.border}`,
                color: T.text,
                fontWeight: 600,
                fontSize: 15,
                textDecoration: "none",
                fontFamily: T.body,
                whiteSpace: "nowrap",
                transition: "background .15s",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLElement).style.background =
                  "rgba(255,255,255,0.09)")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLElement).style.background =
                  "rgba(255,255,255,0.05)")
              }
            >
              How it works
            </a>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ─────────────────────────────────── */}
      <main
        className="main-content"
        style={{
          maxWidth: 1160,
          margin: "0 auto",
          padding: "0 24px 80px",
        }}
      >
        {/* ── UP NEXT (active leagues) ──────────────────── */}
        <section style={{ marginBottom: 56 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 20,
            }}
          >
            <h2
              style={{
                fontFamily: T.display,
                fontSize: 26,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".03em",
                color: T.text,
              }}
            >
              Up Next
            </h2>
          </div>

          {payload.activeLeagues.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {payload.activeLeagues.map((league) => (
                <div key={league.id} className="group">
                  <PublicLeagueCard league={league} user={user} />
                </div>
              ))}
            </div>
          ) : (
            /* empty state — compact horizontal banner */
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: "0 20px",
                height: 110,
                border: "1px dashed rgba(0,197,160,0.22)",
                borderRadius: 14,
                background: "linear-gradient(135deg, rgba(0,197,160,0.04) 0%, transparent 60%)",
                overflow: "hidden",
              }}
            >
              {/* icon */}
              <div
                style={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: T.tealDim,
                  border: "1px solid rgba(0,197,160,0.22)",
                  color: T.teal,
                }}
              >
                <Clock size={20} strokeWidth={1.5} />
              </div>

              {/* title + subtitle */}
              <div style={{ flexShrink: 0 }}>
                <div style={{ fontFamily: T.display, fontSize: 16, fontWeight: 700, textTransform: "uppercase", color: T.text, lineHeight: 1.2 }}>
                  Next game dropping soon
                </div>
                <div style={{ fontFamily: T.body, fontSize: 12, color: T.muted, marginTop: 2 }}>
                  No active pools right now — check back shortly.
                </div>
              </div>

              {/* stats */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginLeft: "auto", flexShrink: 0 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: T.display, fontSize: 26, fontWeight: 800, color: T.teal, lineHeight: 1 }}>
                    {totalPlayers > 0 ? `${totalPlayers.toLocaleString()}+` : "15k+"}
                  </div>
                  <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: T.body }}>players</div>
                </div>
                <div style={{ width: 1, height: 28, background: T.border }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: T.display, fontSize: 18, fontWeight: 800, color: T.teal, lineHeight: 1 }}>60%</div>
                  <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: T.body }}>retention</div>
                </div>
                <div style={{ width: 1, height: 28, background: T.border }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: T.display, fontSize: 18, fontWeight: 800, color: T.teal, lineHeight: 1 }}>Any</div>
                  <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: T.body }}>sport</div>
                </div>
              </div>

              {/* buttons */}
              <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 16 }}>
                <Link
                  to="/signup"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "8px 16px",
                    borderRadius: 8,
                    background: T.teal,
                    color: T.bg,
                    fontWeight: 700,
                    fontSize: 13,
                    textDecoration: "none",
                    fontFamily: T.body,
                    whiteSpace: "nowrap",
                  }}
                >
                  Sign Up
                </Link>
                <a
                  href="#"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "8px 16px",
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.05)",
                    border: `1px solid ${T.border}`,
                    color: T.text,
                    fontWeight: 600,
                    fontSize: 13,
                    textDecoration: "none",
                    fontFamily: T.body,
                    whiteSpace: "nowrap",
                  }}
                >
                  Follow @7Qz
                </a>
              </div>
            </div>
          )}
        </section>

        {/* ── HOW IT WORKS ─────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <h2
            style={{
              fontFamily: T.display,
              fontSize: 26,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".03em",
              color: T.text,
            }}
          >
            How it Works
          </h2>
        </div>
        <div
          id="how-it-works"
          className="how-strip max-sm:!grid-cols-1"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1,
            background: T.border,
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            overflow: "hidden",
            marginBottom: 56,
          }}
        >
          {[
            {
              n: "1",
              head: "Pick any 3 tabs",
              body: "One set of rules — golf, rugby, soccer, F1, you name it.",
            },
            {
              n: "2",
              head: "Points = odds",
              body: "Longer shots score more. 3 bankers or 1 banker + 2 long shots.",
            },
            {
              n: "3",
              head: "Top score wins",
              body: "You don't need all 3 correct. Just outscore everyone else.",
            },
          ].map((step) => (
            <div
              key={step.n}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 14,
                padding: "22px 24px",
                background: T.surface,
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: T.tealDim,
                  border: `1px solid ${T.tealBorder}`,
                  color: T.teal,
                  fontFamily: T.display,
                  fontSize: 15,
                  fontWeight: 800,
                }}
              >
                {step.n}
              </span>
              <div>
                <strong
                  style={{
                    display: "block",
                    fontWeight: 700,
                    fontSize: 14,
                    marginBottom: 4,
                    color: T.text,
                    fontFamily: T.body,
                  }}
                >
                  {step.head}
                </strong>
                <p
                  style={{
                    fontSize: 13,
                    color: T.muted,
                    lineHeight: 1.5,
                    fontFamily: T.body,
                  }}
                >
                  {step.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── RECENT RESULTS (inactive leagues) ─────────── */}
        {payload.inactiveLeagues.length > 0 && (
          <section style={{ marginBottom: 56 }}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 20,
              }}
            >
              <h2
                style={{
                  fontFamily: T.display,
                  fontSize: 26,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".03em",
                  color: T.text,
                }}
              >
                Recent Results
              </h2>
              <Link
                to="/leaderboard"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.teal,
                  textDecoration: "none",
                  opacity: 0.8,
                  fontFamily: T.body,
                  transition: "opacity .15s",
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.opacity = "1")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.opacity = "0.8")
                }
              >
                All results →
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {payload.inactiveLeagues.map((league) => (
                <div key={league.id} className="group">
                  <PublicLeagueCard league={league} user={user} />
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* ── FOOTER ───────────────────────────────────────── */}
      <footer
        style={{
          borderTop: `1px solid ${T.border}`,
          padding: 32,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span
          style={{
            fontSize: 20,
            fontFamily: T.display,
            fontWeight: 800,
            color: T.teal,
            letterSpacing: "-.01em",
          }}
        >
          7<span style={{ color: "#fff" }}>Q</span>z
        </span>
        <p
          style={{
            fontSize: 13,
            color: T.muted,
            maxWidth: 380,
            lineHeight: 1.55,
            fontFamily: T.body,
          }}
        >
          A skill-based, sweepstakes-friendly prediction engine. No bookmaking.
          No odds exposure.
        </p>
        <div style={{ display: "flex", gap: 20 }}>
          {[
            { label: "Terms", href: "#" },
            { label: "Privacy", href: "#" },
            { label: "Contact", href: "mailto:john@7qz.io" },
          ].map((link) => (
            <a
              key={link.label}
              href={link.href}
              style={{
                fontSize: 13,
                color: T.muted,
                textDecoration: "none",
                fontFamily: T.body,
                transition: "color .15s",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLElement).style.color = T.text)
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLElement).style.color = T.muted)
              }
            >
              {link.label}
            </a>
          ))}
        </div>
      </footer>
    </>
  );
}
